import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import React, {useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {FetchPolicy, type Result} from "@khanacademy/wonder-blocks-data";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingLarge,
    LabelMedium,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {calculateNextUrl} from "@khan/auth-shared";
import {BirthdayPicker} from "@khan/component-birthday-picker";
import {useGql, useMutation, useQuery} from "@khan/data-gql";
import {
    gqlOp,
    type GqlOperation,
    type TDataFromGqlOperation,
} from "@khan/data-gql-core";
import {RequestInfo} from "@khan/shared-core";
import {getContinueUrl, redirect, useLocation} from "@khan/urls";
import {RequireReauth} from "@khan/user-settings";

import type {setHasFormalTeacherStatusFixedType} from "./__graphql-types__/index.ts";
import {usePublishExtraInfoSubmittedEvent} from "./extra-info-submitted-cedar-event.ts";
import {
    GET_EXTRA_INFO,
    mapQueryResultToInitialValues,
    UPDATE_EXTRA_INFO,
    type ExtraInfo,
} from "./extra-info.ts";
import {FullNameField} from "./full-name-field.tsx";

const TEACHER_FORMAL_TOS_MUTATION: GqlOperation<
    setHasFormalTeacherStatusFixedType["response"],
    setHasFormalTeacherStatusFixedType["variables"]
> = gqlOp(gql`
    mutation setHasFormalTeacherStatusFixed(
        $tosForFormalTeacherStatus: Boolean
    ) {
        setSettings(tosForFormalTeacherStatus: $tosForFormalTeacherStatus) {
            user {
                id
                tosForFormalTeacherStatus
            }
            errors {
                code
            }
        }
    }
`);

type MutationResult =
    | Result<TDataFromGqlOperation<typeof UPDATE_EXTRA_INFO>>
    // TODO(davemcalpine): add in after A/B Test
    // | Result<TDataFromGqlOperation<typeof SET_SCHOOL_AFFILIATION>>
    // | Result<TDataFromGqlOperation<typeof SET_AFFILIATION_COUNTRY_CODE>>
    | Result<TDataFromGqlOperation<typeof TEACHER_FORMAL_TOS_MUTATION>>
    | undefined
    | null;

interface Props {
    buttonText?: string;
    defaultContinueUrl?: string;
    heading?: string;
    subHeading?: string;
    onSuccessfulSubmit?: () => Promise<unknown>;
    onSuccessfulSubmitStatus?:
        | "loading"
        | "no-data"
        | "error"
        | "aborted"
        | "success"
        | undefined;
    onSuccessfulSubmitError?: string;
    showTosCheckbox?: boolean;
    initialValues?: ExtraInfo;
}

/**
 * A form for the user to enter the extra info required during sign up.
 */
function TeacherExtraInfoForm({
    buttonText = t`Enter Khan Academy for teachers`,
    defaultContinueUrl = "/",
    heading = t`Welcome to Khan Academy for teachers!`,
    subHeading = t`We just need to know a bit more about you to finish setting up your profile.`,
    onSuccessfulSubmit,
    onSuccessfulSubmitStatus,
    onSuccessfulSubmitError,
    showTosCheckbox = false,
    initialValues = {
        nickname: "",
        birthday: "",
        email: "",
        tosForFormalTeacherStatus: false,
    },
}: Props): React.ReactNode {
    const location = useLocation();
    const gqlFetch = useGql();

    const names = getFirstNameLastName(initialValues.nickname);
    const [firstName, setFirstName] = useState<string | null | undefined>(
        names.firstName,
    );
    const [lastName, setLastName] = useState<string | null | undefined>(
        names.lastName,
    );
    const [birthday, setBirthday] = useState<string | null | undefined>(
        initialValues.birthday,
    );
    const [tosChecked, setTosChecked] = useState<boolean | null>(
        showTosCheckbox ? initialValues.tosForFormalTeacherStatus : null,
    );

    // TODO(davemcalpine): add in after A/B Test
    // const [country, setCountry] = useState<string>("");
    // const [school, setSchool] = useState<School | null | undefined>(null);
    const [validationErr, setValidationErr] = useState<string | null>("");
    const publishSubmitEvent = usePublishExtraInfoSubmittedEvent();

    const [updateExtraInfo, extraInfoResult] = useMutation(UPDATE_EXTRA_INFO);
    const [tosMutation, tosMutationResult] = useMutation(
        TEACHER_FORMAL_TOS_MUTATION,
    );

    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    React.useEffect(() => {
        if (birthday) {
            publishAnalyticsEvent({
                eventType: "SignupBirthdayFilled",
                eventSchemaVersion: 1,
                isIOS: RequestInfo.isIOS,
            });
        }
        // NOTE(davemcalpine): We only care about tracking changes to the birthday.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
    }, [birthday]);

    // TODO(davemcalpine): add in after A/B Test
    // const [setSchoolAffiliation, schoolResult] = useMutation(
    //     SET_SCHOOL_AFFILIATION,
    // );
    // const [setAffiliationCountryCode, countryResult] = useMutation(
    //     SET_AFFILIATION_COUNTRY_CODE,
    // );

    const continueUrl = getContinueUrl(location) ?? defaultContinueUrl;

    const extraInfoError = extractErrorFromResult(extraInfoResult);
    const tosError = extractErrorFromResult(tosMutationResult);

    const signupComplete = isSuccess();

    // Navigate when the form is successfully completed
    React.useEffect(() => {
        if (signupComplete) {
            const navigate = async () => {
                const calculatedNextUrl = await calculateNextUrl({
                    gqlFetch,
                    location,
                    loginType: "password",
                    continueUrl,
                    isFirstLogin: false,
                });
                redirect(calculatedNextUrl);
            };
            navigate();
        }
    }, [signupComplete, gqlFetch, location, continueUrl]);

    function isLoading(): boolean {
        return (
            // TODO(davemcalpine): add in after A/B Test
            // schoolResult?.status === "loading" ||
            // countryResult?.status === "loading"
            extraInfoResult?.status === "loading" ||
            onSuccessfulSubmitStatus === "loading"
        );
    }

    function isValid(): boolean {
        return Boolean(birthday && firstName && lastName);
    }

    async function submitHandler(): Promise<void> {
        setValidationErr(null);
        if (!isValid()) {
            setValidationErr(t`Please fill out all required fields.`);
            return;
        }
        // TODO: Move this logic into isValid
        if (showTosCheckbox && !tosChecked) {
            setValidationErr(
                t`Please accept the terms of service to continue.`,
            );
            return;
        }
        await publishSubmitEvent("teacher");
        // TODO(davemcalpine): add in after A/B Test
        // // Set the school affiliation or affiliated country
        // let schoolPromise;
        // if (school) {
        //     schoolPromise = setSchoolAffiliation({
        //         eduorgKeyId: school.id,
        //     });
        // } else if (country) {
        //     schoolPromise = setAffiliationCountryCode({
        //         countryCode: country,
        //     });
        // }

        // Submit the extra info
        const infoResult = await updateExtraInfo({
            nickname: `${firstName} ${lastName}`,
            birthday,
        });

        const infoSuccess =
            infoResult.status === "success" &&
            infoResult.data.setSettings?.errors?.length === 0;

        if (!infoSuccess) {
            return;
        }

        if (showTosCheckbox && tosChecked) {
            const tosResult = await tosMutation({
                tosForFormalTeacherStatus: tosChecked,
            });

            const tosSuccess =
                tosResult.status === "success" &&
                tosResult.data?.setSettings?.errors?.length === 0;

            if (!tosSuccess) {
                return;
            }
        }

        // This must come after updateExtraInfo because it relies on birthday
        // being set.
        // We only want to call it if updateExtraInfo succeeded
        if (onSuccessfulSubmit) {
            await onSuccessfulSubmit();
        }
    }

    // TODO(davemcalpine): add in after A/B Test
    // const schoolError = extractErrorFromResult(schoolResult);
    // const countryError = extractErrorFromResult(countryResult);

    function isSuccess(): boolean {
        // Required
        const extraInfoSuccess =
            extraInfoResult?.status === "success" && !extraInfoError;

        // Optional
        const onSuccessfulSubmitComplete =
            typeof onSuccessfulSubmitStatus !== "undefined" &&
            onSuccessfulSubmitStatus !== "loading" &&
            !onSuccessfulSubmitError;

        // If the ToS checkbox is not being shown, we consider it a success
        const tosSuccess = showTosCheckbox
            ? tosMutationResult?.status === "success" && !tosError
            : true;

        // TODO(davemcalpine): add in after A/B Test
        // const schoolSuccess = school
        //     ? schoolResult?.status === "success" && !schoolError
        //     : true;
        // const countrySuccess = country
        //     ? countryResult?.status === "success" && !countryError
        //     : true;

        return (
            extraInfoSuccess &&
            tosSuccess &&
            // TODO(davemcalpine): add in after A/B Test
            // schoolSuccess &&
            // countrySuccess &&
            onSuccessfulSubmitComplete
        );
    }

    // TODO: Wrap this into a component that can accept an error label
    const tosCheckbox = showTosCheckbox ? (
        <Checkbox
            checked={tosChecked}
            onChange={setTosChecked}
            label={
                <LabelSmall>
                    <Trans>
                        I am a teacher using this service on behalf of a school.
                        I will comply with requirements described in the{" "}
                        <Link href="/about/docs/khan-academy-terms-of-service#school-use">
                            Terms of Service
                        </Link>{" "}
                        for creating and managing Khan Academy School Accounts.
                    </Trans>
                </LabelSmall>
            }
        />
    ) : null;

    const renderForm = (invokeReauth: () => unknown): React.ReactNode => (
        <form
            action=""
            onSubmit={(evt) => {
                evt.preventDefault();
                invokeReauth();
            }}
        >
            <View style={styles.wrapper}>
                <HeadingLarge style={styles.heading}>{heading}</HeadingLarge>
                <LabelMedium style={styles.subHeading}>
                    {subHeading}
                </LabelMedium>
                <FullNameField
                    firstName={firstName}
                    lastName={lastName}
                    setFirstName={setFirstName}
                    setLastName={setLastName}
                    required={true}
                    showRequiredLabel={true}
                />
                <BirthdayPicker
                    label={t`Date of birth`}
                    required={true}
                    defaultValue={birthday || ""}
                    onChange={setBirthday}
                    dropdownStyle={styles.birthdayPicker}
                />

                {/* TODO(davemcalpine): add in after A/B Test */}
                {/* <LabeledSchoolPicker
                    label={t`Where do you teach?`}
                    onCountryChanged={setCountry}
                    selectedCountryCode={country}
                    onSchoolChanged={setSchool}
                    selectedSchool={school}
                    compactLayout={true}
                    showCountryFlag={true}
                /> */}

                {/* TODO(davemcalpine): add in after A/B Test */}
                {/* {schoolError && <ErrorMessage>{schoolError}</ErrorMessage>}
                {countryError && <ErrorMessage>{countryError}</ErrorMessage>} */}
                {tosCheckbox}
                {tosError && <ErrorMessage>{tosError}</ErrorMessage>}
                {validationErr && <ErrorMessage>{validationErr}</ErrorMessage>}
                {extraInfoError && (
                    <ErrorMessage>{extraInfoError}</ErrorMessage>
                )}
                {onSuccessfulSubmitError && (
                    <ErrorMessage>{onSuccessfulSubmitError}</ErrorMessage>
                )}
                <Button type="submit" spinner={isLoading()}>
                    {buttonText}
                </Button>
            </View>
        </form>
    );

    return (
        <RequireReauth
            identifier={initialValues.email}
            onSuccess={async () => {
                await submitHandler();
            }}
            onFailure={() => {}}
        >
            {(invokeReauth) => renderForm(invokeReauth)}
        </RequireReauth>
    );
}

const DataLoader = (
    props: Omit<Props, "initialValues">,
): React.ReactElement => {
    const [result] = useQuery(GET_EXTRA_INFO, {
        fetchPolicy: FetchPolicy.NetworkOnly,
    });

    if (result.status === "loading") {
        return <CircularSpinner style={styles.spinner} />;
    }
    const initialValues = mapQueryResultToInitialValues(result);
    return <TeacherExtraInfoForm initialValues={initialValues} {...props} />;
};

function ErrorMessage({children}: {children: string}): React.ReactNode {
    return (
        <strong
            className={css(styles.errorMessage)}
            aria-live="polite"
            role="alert"
        >
            {children}
        </strong>
    );
}

const errorMessage = (errorCode): string => {
    const message = {
        SETTINGS_NICKNAME_ERROR: t`Your first name or last name is invalid.`,
        SETTINGS_BIRTHDATE_ERROR: t`Your birthdate is invalid.`,
        SETTINGS_FORMAL_TEACHER_TOS_ERROR: t`There was an error accepting the terms of service. Please try again.`,
        COUNTRY_INTERNAL: t`There was an error setting your country. Please try again.`,
        COUNTRY_INVALID_INPUT: t`The country you selected is invalid.`,
        EDUORG_INTERNAL: t`There was an error setting your school. Please try again.`,
        EDUORG_INVALID_INPUT: t`The school you selected is invalid.`,
    }[errorCode];

    return message || genericErrorMessage();
};

function getFirstNameLastName(nickname: string): {
    firstName: string;
    lastName: string;
} {
    const names = nickname.split(" ");
    if (names.length === 1) {
        return {firstName: names[0], lastName: ""};
    }
    if (names.length > 1) {
        return {
            firstName: names.slice(0, names.length - 1).join(" "),
            lastName: names[names.length - 1],
        };
    }
    return {
        firstName: "",
        lastName: "",
    };
}

function extractErrorFromResult(result: MutationResult): string | null {
    if (!result) {
        return null;
    }

    if (result.status === "success" && result.data) {
        const errors: Array<{code: string}> = [];

        if (result.data["setSettings"]?.errors?.length) {
            errors.push(
                ...result.data["setSettings"].errors.map((err) => {
                    return {code: "SETTINGS_" + err?.code};
                }),
            );
        }
        if (result.data["setEduorgAffiliation"]?.error) {
            errors.push({
                code:
                    "EDUORG_" + result.data["setEduorgAffiliation"].error.code,
            });
        }
        if (result.data["userSettingsSetAffiliationCountryCode"]?.error) {
            errors.push({
                code:
                    "COUNTRY_" +
                    result.data["userSettingsSetAffiliationCountryCode"]?.error
                        .code,
            });
        }
        if (errors.length) {
            return errors
                .map((err) => {
                    return errorMessage(err.code);
                })
                .join("\n");
        }

        return null;
    }
    if (result.status === "error") {
        return genericErrorMessage(result.error.message);
    }
    if (result.status === "no-data" || result.status === "aborted") {
        return genericErrorMessage();
    }

    return null;
}

// eslint-disable-next-line react-refresh/only-export-components -- used elsewhere
export function genericErrorMessage(extraMessage: string = ""): string {
    return t`Something went wrong, please try again. ${extraMessage}`;
}

export default DataLoader;

// eslint-disable-next-line react-refresh/only-export-components -- used for testing
export const exportedForTesting = TeacherExtraInfoForm;

const styles = StyleSheet.create({
    wrapper: {
        gap: spacing.medium_16,
    },
    heading: {
        marginBlockEnd: spacing.medium_16,
    },
    subHeading: {
        color: semanticColor.core.foreground.neutral.subtle,
        marginBlockEnd: spacing.medium_16,
    },
    errorMessage: {
        fontWeight: "normal",
        color: semanticColor.core.foreground.critical.subtle,
    },
    birthdayPicker: {
        flex: 1,
    },
    spinner: {
        alignSelf: "center",
    },
});
